import { jobPositionStore } from 'ats/pages/job-positions/JobPositionStore';
import { jobApplicationStore } from 'ats/pages/pipeline/components/JobApplicationStore';
import { applicantStore } from 'ats/pages/applicants/ApplicantStore';
import { candidatesStore } from 'components/candidates/CandidatesStore';
import { candidateSeniorityOptions } from 'components/candidates/profile/scorecards/CandidateScorecardStore';
import { pickPipelineJobApplicationFilters } from 'ats/pages/pipeline/pipelineJobApplicationFilters';
import {
	deserializeColumnState,
	serializeColumnState,
	normalizeColumnStateForCompare
} from './columnState';
import {
	getCurrentUser,
	getCurrentUserId,
	normalizeUserRefForCompare,
	resolveUserRefs,
	serializeUserRefs
} from './userRef';
import { resolveEntityRefs, serializeEntityRefs } from './entityRef';
import {
	deserializeDate,
	deserializeDateRange,
	normalizeEntityRefIds,
	normalizeGlobalQueryState,
	normalizeQueryState,
	normalizeUiState,
	pickQueryForView,
	serializeDate,
	serializeDateRange,
	serializeOptionValues,
	captureSearchFilter,
	resolveSearchFilterForApply
} from './viewStateUtils';
import { syncFilterDrawerProps } from './syncFilterDrawerProps';

const EMPTY_QUERY = { sort: { column: 'createdAt', asc: false } };

const PIPELINE_VIEW_QUERY_KEYS = [
	'text',
	'title',
	'clientId',
	'lastActivity',
	'createdAt',
	'urgency',
	'priority',
	'status',
	'disabled',
	'owner',
	'hiringManager',
	'accountManager',
	'timezones',
	'id',
	'mineOnly'
];

const PIPELINE_ENTITY_REF_KEYS = [
	'clientIds',
	'hiringManagerIds',
	'timezoneIds',
	'countryIds',
	'tagIds',
	'applicantIds',
	'referrerIds',
	'skillIds'
];

const PIPELINE_USER_REF_KEYS = [
	'ownerFilter',
	'accountManagerFilter',
	'candidateOwnerFilter',
	'sourceFilter'
];

function pickPipelineQueryForView(query) {
	const rest = pickQueryForView(query);
	const picked = {
		sort: rest.sort || EMPTY_QUERY.sort
	};

	PIPELINE_VIEW_QUERY_KEYS.forEach(key => {
		const value = rest[key];
		if (value == null) return;
		if (Array.isArray(value) && value.length === 0) return;
		if (typeof value === 'string' && value.trim() === '') return;
		picked[key] = value;
	});

	return picked;
}

function getDefaultStatusFilter() {
	return [
		{
			...jobPositionStore.getStatusByValue('open'),
			label: 'Open'
		}
	];
}

export function getPipelineFactoryUiState() {
	return {
		searchFilter: null,
		clientFilter: [],
		lastActivityFilter: null,
		createdOnFilter: null,
		urgencyFilter: null,
		priorityFilter: null,
		statusFilter: getDefaultStatusFilter(),
		disabledFilter: null,
		ownerFilter: null,
		hiringManagerFilter: null,
		accountManagerFilter: null,
		timezoneFilter: null,
		jobIdFilter: [],
		stageFilter: null,
		internalStageFilter: [],
		seniorityFilter: [],
		englishLevelFilter: [],
		cityFilter: null,
		countryFilter: null,
		applicantFilter: null,
		candidateOwnerFilter: null,
		sourceFilter: null,
		referrerFilter: null,
		currentCompensationFilter: null,
		expectedCompensationFilter: null,
		tagFilter: null,
		skillFilter: [],
		mineOnly: false
	};
}

export function capturePipelineListView(page) {
	const currentUserId = getCurrentUserId();

	return {
		ui: {
			searchFilter: captureSearchFilter(
				page.state.searchFilter,
				page.listStore.store.query
			),
			clientIds: serializeEntityRefs(page.state.clientFilter),
			lastActivityFilter: serializeDate(page.state.lastActivityFilter),
			createdOnFilter: serializeDateRange(page.state.createdOnFilter),
			urgencyFilter: page.state.urgencyFilter?.value ?? null,
			priorityFilter: serializeOptionValues(page.state.priorityFilter),
			statusFilter: serializeOptionValues(page.state.statusFilter),
			disabledFilter: page.state.disabledFilter,
			ownerFilter: serializeUserRefs(page.state.ownerFilter, currentUserId),
			hiringManagerIds: serializeEntityRefs(page.state.hiringManagerFilter),
			accountManagerFilter: serializeUserRefs(
				page.state.accountManagerFilter,
				currentUserId
			),
			timezoneIds: serializeEntityRefs(page.state.timezoneFilter, 'text'),
			jobIdFilter: page.state.jobIdFilter || null,
			stageFilter: serializeOptionValues(page.state.stageFilter),
			internalStageFilter: serializeOptionValues(
				page.state.internalStageFilter
			),
			seniorityFilter: serializeOptionValues(page.state.seniorityFilter),
			englishLevelFilter: serializeOptionValues(page.state.englishLevelFilter),
			cityFilter: page.state.cityFilter || null,
			countryIds: serializeEntityRefs(page.state.countryFilter),
			applicantIds: serializeEntityRefs(page.state.applicantFilter),
			candidateOwnerFilter: serializeUserRefs(
				page.state.candidateOwnerFilter,
				currentUserId
			),
			sourceFilter: serializeUserRefs(page.state.sourceFilter, currentUserId),
			referrerIds: serializeEntityRefs(page.state.referrerFilter),
			currentCompensationFilter: page.state.currentCompensationFilter || null,
			expectedCompensationFilter: page.state.expectedCompensationFilter || null,
			tagIds: serializeEntityRefs(page.state.tagFilter),
			skillIds: serializeEntityRefs(page.state.skillFilter),
			mineOnly: !!page.state.mineOnly
		},
		query: pickPipelineQueryForView(page.listStore.store.query),
		globalQuery: pickPipelineJobApplicationFilters(
			jobApplicationStore.globalQuery
		),
		columnState: serializeColumnState(page.state.visibleColumns)
	};
}

export function getPipelineFactorySnapshot() {
	return {
		ui: {
			searchFilter: null,
			clientIds: null,
			lastActivityFilter: null,
			createdOnFilter: null,
			urgencyFilter: null,
			priorityFilter: null,
			statusFilter: ['open'],
			disabledFilter: null,
			ownerFilter: null,
			hiringManagerIds: null,
			accountManagerFilter: null,
			timezoneIds: null,
			jobIdFilter: null,
			stageFilter: null,
			internalStageFilter: null,
			seniorityFilter: null,
			englishLevelFilter: null,
			cityFilter: null,
			countryIds: null,
			applicantIds: null,
			candidateOwnerFilter: null,
			sourceFilter: null,
			referrerIds: null,
			currentCompensationFilter: null,
			expectedCompensationFilter: null,
			tagIds: null,
			skillIds: null,
			mineOnly: false
		},
		query: {
			sort: { column: 'createdAt', asc: false },
			status: ['open']
		},
		globalQuery: {},
		columnState: serializeColumnState(
			jobApplicationStore.defaultTableColumnsOptions
		)
	};
}

function mapSeniorityOptions(values) {
	return values
		?.map(value => {
			const option = candidateSeniorityOptions.find(
				item => item.value === value
			);
			return option ? { ...option, label: option.text } : null;
		})
		.filter(Boolean);
}

function buildUiStateFromSaved(ui) {
	const currentUser = getCurrentUser();

	return {
		searchFilter: ui.searchFilter || null,
		clientFilter: resolveEntityRefs(ui.clientIds) || [],
		lastActivityFilter: deserializeDate(ui.lastActivityFilter),
		createdOnFilter: deserializeDateRange(ui.createdOnFilter),
		urgencyFilter: ui.urgencyFilter
			? {
					...jobPositionStore.getUrgencyByValue(ui.urgencyFilter),
					label: jobPositionStore.getUrgencyByValue(ui.urgencyFilter)?.text
				}
			: null,
		priorityFilter:
			ui.priorityFilter
				?.map(value => {
					const option = jobPositionStore.priorityOptions.find(
						priority => priority.value === value
					);
					return option ? { ...option, label: option.text } : null;
				})
				.filter(Boolean) || null,
		statusFilter:
			ui.statusFilter == null
				? null
				: ui.statusFilter
						?.map(value => {
							const option = jobPositionStore.getStatusByValue(value);
							return option ? { ...option, label: 'Open' } : null;
						})
						.filter(Boolean) || [],
		disabledFilter: ui.disabledFilter ?? null,
		ownerFilter: resolveUserRefs(ui.ownerFilter, currentUser),
		hiringManagerFilter: resolveEntityRefs(ui.hiringManagerIds) || null,
		accountManagerFilter: resolveUserRefs(ui.accountManagerFilter, currentUser),
		timezoneFilter: resolveEntityRefs(ui.timezoneIds, 'text') || null,
		jobIdFilter: ui.jobIdFilter || [],
		stageFilter:
			ui.stageFilter
				?.map(value =>
					jobApplicationStore.stageOptions.find(stage => stage.value === value)
				)
				.filter(Boolean) || null,
		internalStageFilter:
			ui.internalStageFilter
				?.map(value =>
					applicantStore.stageOptions.find(stage => stage.value === value)
				)
				.filter(Boolean) || [],
		seniorityFilter: mapSeniorityOptions(ui.seniorityFilter) || [],
		englishLevelFilter:
			ui.englishLevelFilter
				?.map(value => {
					const option = candidatesStore.englishLevelOptions.find(
						level => level.value === value
					);
					return option ? { ...option, label: option.text } : null;
				})
				.filter(Boolean) || [],
		cityFilter: ui.cityFilter || null,
		countryFilter: resolveEntityRefs(ui.countryIds) || null,
		applicantFilter: resolveEntityRefs(ui.applicantIds) || null,
		candidateOwnerFilter: resolveUserRefs(ui.candidateOwnerFilter, currentUser),
		sourceFilter: resolveUserRefs(ui.sourceFilter, currentUser),
		referrerFilter: resolveEntityRefs(ui.referrerIds) || null,
		currentCompensationFilter: ui.currentCompensationFilter || null,
		expectedCompensationFilter: ui.expectedCompensationFilter || null,
		tagFilter: resolveEntityRefs(ui.tagIds) || null,
		skillFilter: resolveEntityRefs(ui.skillIds) || null,
		mineOnly: !!ui.mineOnly
	};
}

function applyJobQueryFields(page, query) {
	if (!query) return;
	const fields = [
		'title',
		'text',
		'clientId',
		'lastActivity',
		'createdAt',
		'urgency',
		'priority',
		'status',
		'disabled',
		'owner',
		'hiringManager',
		'accountManager',
		'timezones',
		'id',
		'mineOnly',
		'jobApplicationFilters'
	];
	fields.forEach(field => {
		if (query[field] != null) {
			page.listStore.store.setQuery(field, query[field]);
		}
	});
}

function applyGlobalQuery(globalQuery) {
	jobApplicationStore.resetGlobalQuery();
	Object.entries(globalQuery || {}).forEach(([key, value]) => {
		if (value != null) {
			jobApplicationStore.setGlobalQuery(key, value);
		}
	});
}

export function applyPipelineListView(
	page,
	snapshot,
	{ isFactoryDefault = false } = {}
) {
	const ui = buildUiStateFromSaved(snapshot.ui || {});
	ui.searchFilter = resolveSearchFilterForApply(
		ui.searchFilter,
		snapshot.query
	);
	const visibleColumns = isFactoryDefault
		? jobApplicationStore.defaultTableColumnsOptions
		: deserializeColumnState(
				snapshot.columnState,
				jobApplicationStore.tableColumnsOptions,
				jobApplicationStore.defaultTableColumnsOptions
			);

	page.applyingListView = true;
	try {
		page.listStore.store.resetQuery();
		page.listStore.store.setQuery(
			'sort',
			snapshot.query?.sort || { column: 'createdAt', asc: false }
		);
		page.listStore.store.setQuery('title', ui.searchFilter || null);
		applyJobQueryFields(page, snapshot.query);
		applyGlobalQuery(snapshot.globalQuery);
		page.listStore.store.setQuery('mineOnly', ui.mineOnly ? true : null);
		jobApplicationStore.setGlobalQuery('mineOnly', ui.mineOnly ? true : null);
		page.syncJobApplicationFiltersToJobQuery();
	} finally {
		page.applyingListView = false;
	}

	return new Promise(resolve => {
		page.setState({ ...ui, visibleColumns }, () => {
			syncFilterDrawerProps(page);
			page.handlePaginationChange(1);
			resolve();
		});
	});
}

export function applyPipelineFactoryDefaults(page) {
	return applyPipelineListView(page, getPipelineFactorySnapshot(), {
		isFactoryDefault: true
	});
}

export function resolvePipelineViewSnapshot(view) {
	if (view.systemKey === 'default') {
		return getPipelineFactorySnapshot();
	}
	return {
		ui: view.filterState?.ui || {},
		query: view.filterState?.query || EMPTY_QUERY,
		globalQuery: view.filterState?.globalQuery || {},
		columnState: view.columnState
	};
}

export function normalizePipelineSnapshot(snapshot) {
	const ui = normalizeUiState(snapshot.ui || {});

	PIPELINE_ENTITY_REF_KEYS.forEach(key => {
		if (ui[key] != null) {
			ui[key] = normalizeEntityRefIds(ui[key]);
		}
	});
	PIPELINE_USER_REF_KEYS.forEach(key => {
		if (ui[key] != null) {
			ui[key] = normalizeUserRefForCompare(ui[key]);
		}
	});

	return {
		ui,
		query: normalizeQueryState(
			pickPipelineQueryForView(snapshot.query || {}),
			EMPTY_QUERY.sort
		),
		globalQuery: normalizeGlobalQueryState(snapshot.globalQuery),
		columnState: normalizeColumnStateForCompare(
			snapshot.columnState,
			jobApplicationStore.defaultTableColumnsOptions
		)
	};
}

export function serializePipelineViewForSave(snapshot) {
	const query = { ...(snapshot.query || {}) };

	if (snapshot.ui?.mineOnly) {
		query.mineOnly = true;
	} else {
		delete query.mineOnly;
	}

	return {
		filterState: {
			ui: snapshot.ui,
			query,
			globalQuery: pickPipelineJobApplicationFilters(snapshot.globalQuery || {})
		},
		columnState: snapshot.columnState
	};
}
